import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { ApiError } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { createCheckoutSession, formatPrice, simulatePayment } from '../booking/bookingApi';
import { paymentConfirmingId } from '../booking/checkoutHandoff';
import { usePaymentReturn } from './usePaymentReturn';
import { fetchOutstanding, fmtSlot, minutesLeft, OUTSTANDING_KEY } from './outstanding';
import './payment-prompt.css';
import '../booking/booking.css';

/**
 * Everything the signed-in coach still owes for, in one place — the page the after-login nudge
 * summarises. Same data and the same Pay now flow as the nudge: only the booking's creator can
 * open its checkout session, so every row here is genuinely payable by this coach.
 */
export function MyPaymentsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [payingId, setPayingId] = useState<number | null>(null);

  const outstanding = useQuery({ queryKey: OUTSTANDING_KEY, queryFn: fetchOutstanding });

  // The Pay now button keeps spinning past createCheckoutSession resolving (the browser is still
  // leaving for Stripe); if the coach comes back without paying nothing reloads, so un-stick it
  // and reconcile with Stripe in case they in fact paid (see usePaymentReturn).
  usePaymentReturn(payingId, () => setPayingId(null));

  async function pay(bookingId: number) {
    setPayingId(bookingId);
    try {
      const session = await createCheckoutSession(bookingId);
      window.location.href = session.url;
    } catch (err) {
      if (err instanceof ApiError && err.code === 'payments-not-configured') {
        try {
          await simulatePayment(bookingId);
          toast('Payment simulated (dev mode) — booking confirmed', 'success');
          void queryClient.invalidateQueries({ queryKey: OUTSTANDING_KEY });
        } catch {
          toast('Online payment is not set up yet — contact the club', 'error');
        }
      } else {
        toast(err instanceof ApiError ? err.message : 'Could not open checkout — try again', 'error');
      }
      setPayingId(null);
    }
  }

  // A booking whose payment is being reconciled with Stripe (the coach just landed back from
  // checkout) is hidden rather than offered for payment again — the outstanding endpoint still
  // lists it until the confirmation commits.
  const confirming = paymentConfirmingId();
  const rows = (outstanding.data ?? []).filter((r) => r.bookingId !== confirming);
  const total = rows.reduce((sum, r) => sum + r.pricePence, 0);

  return (
    <div className="my-bookings">
      <Card
        title="Payments"
        subtitle={
          rows.length === 0
            ? undefined
            : `${rows.length} booking${rows.length === 1 ? '' : 's'} awaiting payment — ${formatPrice(total)} in total`
        }
      >
        {outstanding.isLoading ? (
          <Spinner center />
        ) : rows.length === 0 ? (
          <p className="my-payments-empty">
            <CheckCircle2 size={16} aria-hidden />
            <span>Nothing outstanding — you&apos;re all paid up.</span>
          </p>
        ) : (
          <ul className="pay-prompt-list">
            {rows.map((row) => {
              const left = minutesLeft(row.holdExpiresUtc);
              return (
                <li key={row.bookingId} className="pay-prompt-row">
                  <div className="pay-prompt-detail">
                    <span className="pay-prompt-team">{row.teamName}</span>
                    <span className="pay-prompt-slot">
                      {row.pitchName} · {fmtSlot(row.startUtc, row.endUtc)}
                    </span>
                    <span className="pay-prompt-venue">{row.venueName}</span>
                    {left !== null ? (
                      <span className={`pay-prompt-hold ${left <= 5 ? 'pay-prompt-hold-urgent' : ''}`}>
                        {left === 0 ? 'Hold about to expire' : `Held for ${left} more minute${left === 1 ? '' : 's'}`}
                      </span>
                    ) : null}
                  </div>
                  <div className="pay-prompt-action">
                    <span className="pay-prompt-price">{formatPrice(row.pricePence)}</span>
                    <Button
                      size="sm"
                      loading={payingId === row.bookingId}
                      disabled={payingId !== null}
                      onClick={() => void pay(row.bookingId)}
                    >
                      Pay now
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
